import * as React from "react"
import { Head, usePage } from "@inertiajs/react"
import { type Components } from "react-markdown"
import PublicLayout from "@/components/PublicLayout"
import { HeroBand, type HeroPhoto } from "@/components/gisela/HeroBand"
import { ContactBand, PhotoEnquiryBand } from "@/components/gisela/ClosingBands"
import GiselaPageTiles from "@/components/gisela/GiselaPageTiles"
import {
  RelatedArticlesBand,
  RelatedArticlesSidebar,
  type RelatedArticle,
} from "@/components/gisela/RelatedArticles"
import { TextBand, bandBg } from "@/components/gisela/TextBand"
import { useLocalePath } from "@/lib/locale-path"
import { cn } from "@/lib/utils"
import { presentBands } from "@/lib/section-order"
import { sectionHidden } from "@/lib/section-visibility"
import type { PageMeta, PageProps, PlacementPhoto } from "@/types/inertia"

interface Photo {
  id: number
  url: string | null
  alt_text: string | null
}

interface Props {
  therapy: { slug: string; label: string }
  content: {
    hero: { heading?: string; subheading?: string; photo?: HeroPhoto | null; _hidden?: boolean }
    helps_with: { heading?: string; body?: string }
    how_it_works: { heading?: string; body?: string; photo?: Photo }
    expect: { heading?: string; body?: string }
    faq: { body?: string }
    // The closing pair every page of hers ends on.
    cta?: { photo?: HeroPhoto | null; _hidden?: boolean }
    contact?: { heading?: string; body?: string; _hidden?: boolean }
  }
  articles?: RelatedArticle[]
  /** The home page's tile slot — one card per page of her site. */
  tile_photos?: Record<string, PlacementPhoto | null>
  /** A room of hers, standing beside the short enquiry form. */
  contact_photo?: PlacementPhoto | null
  /** Registry entry ids in display order — the admin's arrangement. */
  section_order?: string[]
  meta?: PageMeta
}

// Shared template for every therapy landing page (Therapy rows) — CBT, EMDR,
// I-CBT, ACT, IFS, and any added from the admin. The sibling of
// ConditionPage: same shape, but these pages answer "how do you work?" rather
// than "what's wrong?". All copy is CMS-driven; the controller supplies
// per-therapy fallback metadata.
export default function TherapyPage({
  therapy,
  content,
  articles,
  tile_photos = {},
  contact_photo,
  section_order,
  meta,
}: Props) {
  const { props } = usePage<PageProps>()
  const t = props.translations.gisela_pages.therapy
  const tc = props.translations.gisela_pages.common
  const lp = useLocalePath()
  const title = meta?.title ?? therapy.label
  const description = meta?.description ?? ""

  // The seeded copy cross-links to her condition pages — keep those links on
  // the visitor's language rather than dropping them back into English.
  const markdown: Components = {
    a: ({ href, children }) => <a href={lp(href ?? "")}>{children}</a>,
  }

  // Entry ids are generated per therapy (Therapy.registry_entries):
  // "<slug>-helps-with" and so on.
  const id = (key: string) => `${therapy.slug}-${key}`
  const bands = presentBands(
    [
      { id: id("helps-with"), present: !!content.helps_with.body,
        render: (i) => <TextBand index={i} heading={content.helps_with.heading || t.helps_with_heading} body={content.helps_with.body} markdown={markdown} /> },
      { id: id("how-it-works"), present: !!content.how_it_works.body,
        render: (i) => <TextBand index={i} heading={content.how_it_works.heading || t.how_it_works_heading} body={content.how_it_works.body} photo={content.how_it_works.photo} markdown={markdown} /> },
      { id: id("expect"), present: !!content.expect.body,
        render: (i) => (
          <TextBand index={i} heading={content.expect.heading || t.expect_heading} body={content.expect.body} markdown={markdown}>
            {/* Every therapy here is offered online, and the online page is
                where that question gets answered properly. A standing line
                rather than seeded copy, so all five therapies carry it and
                neither language can drift. */}
            <p className="mt-4">
              <a href={lp("/online-therapy")}>{t.online_link}</a>
            </p>
          </TextBand>
        ) },
      { id: id("faq"), present: !!content.faq.body,
        render: (i) => <TextBand index={i} heading={tc.common_questions} body={content.faq.body} markdown={markdown} /> },
    ],
    section_order,
  )

  return (
    <>
      <Head title={title}>
        {description && <meta name="description" content={description} />}
        <meta property="og:title" content={title} />
        {description && <meta property="og:description" content={description} />}
      </Head>
      <PublicLayout>
        {/* Hero */}
        {!sectionHidden(content.hero) && (
          <HeroBand photo={content.hero.photo}>
            <p className="mb-3 text-xs font-medium uppercase tracking-widest text-white/60">
              {t.eyebrow}
            </p>
            <h1 className="text-white">{content.hero.heading || therapy.label}</h1>
            {content.hero.subheading && (
              <p className="mt-4 max-w-2xl text-lg text-white/80">{content.hero.subheading}</p>
            )}
          </HeroBand>
        )}

        {/* Everything below the hero reserves the right gutter (xl+) for the
            related-articles sidebar; the bands' full-bleed backgrounds still
            span the viewport because the padding sits on each section. */}
        {/* The gutter keeps a column's worth of height when the sidebar is
            in it: a page whose bands are shorter than its reading list would
            otherwise leave the sticky column nowhere to sit, and it would
            spill over the bands the page closes with. */}
        <div
          className={cn(
            "relative xl:[&>section]:pr-80",
            !!articles?.length && "xl:min-h-[34rem]",
          )}
        >
          {bands.map((band, i) => (
            <React.Fragment key={band.id}>{band.render(i)}</React.Fragment>
          ))}

          {/* Articles written for this therapy — self-maintaining, and absent
              until one is linked to the page. A band below xl, a sidebar above. */}
          <RelatedArticlesBand articles={articles} className={bandBg(bands.length)} />

          <RelatedArticlesSidebar articles={articles} />
        </div>

        {/* The close every page of hers ends on — the invitation, the tiles
            out of it, then a room beside the short form. These sit outside
            the sidebar's gutter: they are full-width bands, and the reading
            column they would otherwise be squeezed into has finished by
            here. */}
        {!sectionHidden(content.cta) && (
          <ContactBand heading={t.cta_heading} body={t.cta_body} photo={content.cta?.photo} />
        )}

        {/* This page's own tile is not in the row to begin with — the row is
            her top-level pages. */}
        <GiselaPageTiles photos={tile_photos} />

        {!sectionHidden(content.contact) && (
          <PhotoEnquiryBand
            heading={content.contact?.heading}
            body={content.contact?.body}
            photo={contact_photo}
          />
        )}
      </PublicLayout>
    </>
  )
}
